import { Schema } from 'prosemirror-model';
import { nodes, marks } from 'ngx-editor/schema';
import { EditorState, Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

// ── Image node with style attribute support ───────────────────────────────────
const imageNodeSpec = {
  inline: true,
  attrs: {
    src:   {},
    alt:   { default: null },
    title: { default: null },
    style: { default: null },
  },
  group: 'inline',
  draggable: true,
  parseDOM: [{
    tag: 'img[src]',
    getAttrs(dom: Element): Record<string, string | null> {
      return {
        src:   dom.getAttribute('src'),
        alt:   dom.getAttribute('alt'),
        title: dom.getAttribute('title'),
        style: dom.getAttribute('style'),
      };
    },
  }],
  toDOM(node: any): any {
    const { src, alt, title, style } = node.attrs;
    const attrs: Record<string, string> = { src };
    if (alt)   attrs['alt']   = alt;
    if (title) attrs['title'] = title;
    if (style) attrs['style'] = style;
    return ['img', attrs];
  },
};
import {
  tableNodes,
  tableEditing,
  columnResizing,
  addColumnAfter,
  addColumnBefore,
  deleteColumn,
  addRowAfter,
  addRowBefore,
  deleteRow,
  deleteTable,
} from 'prosemirror-tables';

// ── Schema ────────────────────────────────────────────────────────────────────

export const tableSchema = new Schema({
  nodes: {
    ...nodes,
    image: imageNodeSpec,          // override with style-aware image node
    ...tableNodes({
      tableGroup: 'block',
      cellContent: 'block+',
      cellAttributes: {},
    }),
  },
  marks,
});

// ── Word paste cleanup ────────────────────────────────────────────────────────
//
// Strategy: use DOMParser (available in browser) for structured manipulation.
// Sequence:
//   1. Remove Word metadata (style blocks, xml blocks)
//   2. Unwrap WordSection wrapper divs
//   3. Convert Word heading paragraphs → <h1>–<h6>
//   4. Convert Word list paragraphs → <ol>/<ul>/<li>   ← main layout fix
//   5. Clean every element's attributes (strip mso-* styles, keep formatting)
//   6. Remove empty leftovers
// ─────────────────────────────────────────────────────────────────────────────

// Inline CSS props we want to KEEP so ngx-editor can read them:
//   font-weight  → strong mark (reads style.fontWeight)
//   font-style   → em mark     (reads style.fontStyle)
//   text-decoration → u/s mark (reads style.textDecoration)
//   text-align   → paragraph align attr (reads dom.style.textAlign)
//   color / background-color → text_color / background_color marks
const KEEP_STYLE_PROPS = new Set([
  'font-weight', 'font-style', 'text-decoration',
  'text-align', 'color', 'background-color',
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-color', 'border-style', 'border-width',
]);

// Word paragraph class → semantic heading tag
const WORD_HEADING_MAP: Record<string, string> = {
  MsoTitle:    'h1',
  MsoHeading1: 'h1',
  MsoHeading2: 'h2',
  MsoHeading3: 'h3',
  MsoHeading4: 'h4',
  MsoHeading5: 'h5',
  MsoHeading6: 'h6',
};

// Characters considered list markers (bullet symbols)
const BULLET_RE = /^[·•◦▪▸►▷○●\-–—]$/;

// ── Helpers ───────────────────────────────────────────────────────────────────

function keepStyleAttr(style: string): string {
  const kept = style
    .split(';')
    .map(s => s.trim())
    .filter(s => {
      if (!s) return false;
      const prop = s.split(':')[0].trim().toLowerCase();
      return prop && KEEP_STYLE_PROPS.has(prop);
    });
  return kept.join('; ');
}

/** Replace a paragraph with a heading of the given tag, preserving content & alignment. */
function replaceWithHeading(doc: Document, p: Element, tag: string): void {
  const h = doc.createElement(tag);
  h.innerHTML = p.innerHTML;
  const align = (p as HTMLElement).style.textAlign || p.getAttribute('align');
  if (align) h.style.textAlign = align;
  p.replaceWith(h);
}

/**
 * Convert consecutive <p class="MsoListParagraph*"> into proper <ol>/<ul>/<li>.
 * Word wraps the list marker (e.g. "1." or "•") in a <span> at the start of
 * each paragraph. We detect and strip that marker span.
 */
function convertWordLists(doc: Document): void {
  let currentList: HTMLElement | null = null;
  let currentListTag = '';

  // Snapshot all paragraphs before we start mutating the DOM
  const allParas = Array.from(doc.querySelectorAll('p')) as HTMLParagraphElement[];

  for (const p of allParas) {
    if (!p.parentNode) continue;   // already removed

    const cls = p.className || '';
    const styleAttr = p.getAttribute('style') || '';
    const isWordList =
      /MsoListParagraph/i.test(cls) || /mso-list\s*:/i.test(styleAttr);

    if (!isWordList) {
      currentList = null;
      continue;
    }

    // ── Detect list type ────────────────────────────────────────────────────
    // Normalise non-breaking spaces + tabs before testing
    const rawText = (p.textContent || '').replace(/[\u00a0\t]+/g, ' ').trim();
    const isNumbered = /^\d+[.)]\s/.test(rawText);
    const listTag = isNumbered ? 'ol' : 'ul';

    // ── Create / reuse list element ──────────────────────────────────────────
    if (!currentList || currentListTag !== listTag) {
      currentList = doc.createElement(listTag);
      currentListTag = listTag;
      p.parentNode.insertBefore(currentList, p);
    }

    // ── Build the <li> content (strip leading marker span) ──────────────────
    const li = doc.createElement('li');
    const temp = doc.createElement('div');
    temp.innerHTML = p.innerHTML;

    // Walk the leading children and remove spans that contain only a marker
    for (const child of Array.from(temp.childNodes)) {
      if (child.nodeType !== Node.ELEMENT_NODE) break;
      const el = child as Element;
      if (el.tagName !== 'SPAN') break;

      // Collapse whitespace and non-breaking spaces to check marker content
      const spanText = (el.textContent || '')
        .replace(/[\u00a0\s\t\ufeff]/g, '');

      const isMarker =
        spanText === '' ||
        /^\d+[.)]$/.test(spanText) ||        // "1."  "2)"
        BULLET_RE.test(spanText);             // "•"  "·"  "▸" etc.

      if (isMarker) {
        temp.removeChild(child);
      } else {
        break; // Stop as soon as we hit a non-marker span
      }
    }

    li.innerHTML = temp.innerHTML.trim();
    currentList.appendChild(li);
    p.remove();
  }
}

// ── Main cleanup function ─────────────────────────────────────────────────────

function cleanWordHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // 1. Remove metadata elements entirely
  doc.querySelectorAll('style, xml').forEach(el => el.remove());

  // 2. Unwrap WordSection wrapper divs (transparent wrapper Word adds)
  doc.querySelectorAll('div[class*="WordSection"], div[class*="Section"]')
    .forEach(div => div.replaceWith(...Array.from(div.childNodes)));

  // 3. Convert Word heading paragraphs to semantic <h1>–<h6>
  doc.querySelectorAll('p[class]').forEach(p => {
    const cls = p.className || '';
    for (const [wordClass, tag] of Object.entries(WORD_HEADING_MAP)) {
      if (cls.includes(wordClass)) {
        replaceWithHeading(doc, p, tag);
        break;
      }
    }
  });

  // 4. Convert Word list paragraphs to proper <ol>/<ul>/<li>
  convertWordLists(doc);

  // 5. Clean every element's attributes
  doc.querySelectorAll('*').forEach(el => {
    const htmlEl = el as HTMLElement;

    // Clean inline styles — keep only formatting-relevant properties
    const raw = htmlEl.getAttribute('style');
    if (raw) {
      const cleaned = keepStyleAttr(raw);
      if (cleaned) {
        htmlEl.setAttribute('style', cleaned);
      } else {
        htmlEl.removeAttribute('style');
      }
    }

    // Remove Word Mso* class attributes (keep non-Mso classes)
    const cls = el.getAttribute('class') || '';
    if (/Mso/i.test(cls)) el.removeAttribute('class');

    // Remove lang attributes
    el.removeAttribute('lang');
  });

  // 6. Unwrap Office namespace inline tags (o:p etc.)
  doc.querySelectorAll('o\\:p').forEach(el =>
    el.replaceWith(...Array.from(el.childNodes))
  );

  // 7. Remove leftover empty elements
  doc.querySelectorAll('span:empty, p:empty').forEach(el => el.remove());

  return doc.body.innerHTML;
}

// ── Word paste plugin ─────────────────────────────────────────────────────────

const wordPastePlugin = new Plugin({
  props: {
    transformPastedHTML(html: string): string {
      if (/mso-|MsoNormal|MsoHeading|MsoList|WordSection|urn:schemas-microsoft|<o:p/i.test(html)) {
        return cleanWordHtml(html);
      }
      return html;
    },
  },
});

// ── Plugins ───────────────────────────────────────────────────────────────────

export const tablePlugins: Plugin[] = [
  columnResizing(),
  tableEditing(),
  wordPastePlugin,
];

// ── Table creation ────────────────────────────────────────────────────────────

function buildTable(schema: Schema, rows = 3, cols = 3) {
  const headerRow = schema.nodes['table_row'].create(
    null,
    Array.from({ length: cols }, () => schema.nodes['table_header'].createAndFill()!)
  );
  const bodyRows = Array.from({ length: rows - 1 }, () =>
    schema.nodes['table_row'].create(
      null,
      Array.from({ length: cols }, () => schema.nodes['table_cell'].createAndFill()!)
    )
  );
  return schema.nodes['table'].create(null, [headerRow, ...bodyRows]);
}

function insertTableCmd(state: EditorState, dispatch?: EditorView['dispatch']): boolean {
  if (dispatch) {
    dispatch(state.tr.replaceSelectionWith(buildTable(state.schema)));
  }
  return true;
}

// ── Custom toolbar button factory ─────────────────────────────────────────────

type Command = (state: EditorState, dispatch?: any) => boolean;
type CustomItem = (view: EditorView) => { dom: HTMLElement; update: (state: EditorState) => void };

function makeTableBtn(label: string, title: string, cmd: Command): CustomItem {
  return (view: EditorView) => {
    const dom = document.createElement('div');
    dom.className = 'NgxEditor__MenuItem NgxEditor__MenuItem--Button';
    dom.setAttribute('title', title);
    dom.textContent = label;
    dom.style.cssText =
      'padding: 0 6px; cursor: pointer; font-size: 12px; white-space: nowrap; line-height: 24px;';

    dom.addEventListener('mousedown', (e) => {
      e.preventDefault();
      cmd(view.state, view.dispatch);
      view.focus();
    });

    return {
      dom,
      update(state: EditorState) {
        const enabled = cmd(state, undefined);
        dom.style.opacity = enabled ? '1' : '0.35';
        dom.style.pointerEvents = enabled ? 'auto' : 'none';
      },
    };
  };
}

// ── Exported toolbar items ────────────────────────────────────────────────────

export const insertTableBtn  = makeTableBtn('⊞ Table',  'Insert Table (3×3)',   insertTableCmd);
export const addColAfterBtn  = makeTableBtn('+Col→',    'Add Column After',     addColumnAfter);
export const addColBeforeBtn = makeTableBtn('←Col+',    'Add Column Before',    addColumnBefore);
export const delColBtn       = makeTableBtn('−Col',     'Delete Column',        deleteColumn);
export const addRowAfterBtn  = makeTableBtn('+Row↓',    'Add Row After',        addRowAfter);
export const addRowBeforeBtn = makeTableBtn('↑Row+',    'Add Row Before',       addRowBefore);
export const delRowBtn       = makeTableBtn('−Row',     'Delete Row',           deleteRow);
export const delTableBtn     = makeTableBtn('⊠ Table',  'Delete Table',         deleteTable);

// ── Custom image insert button ────────────────────────────────────────────────

export const insertImageBtn: CustomItem = (view: EditorView) => {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position: relative; display: inline-block;';

  const btn = document.createElement('div');
  btn.className = 'NgxEditor__MenuItem NgxEditor__MenuItem--Button';
  btn.setAttribute('title', 'Insert Image');
  btn.style.cssText = 'padding: 0 6px; cursor: pointer; font-size: 12px; white-space: nowrap; line-height: 24px;';
  btn.textContent = '🖼 Image';

  const panel = document.createElement('div');
  panel.style.cssText = [
    'position:absolute; top:calc(100% + 2px); left:0; z-index:200;',
    'background:#fff; border:1px solid #ccc; border-radius:4px;',
    'padding:10px; width:260px; display:none;',
    'box-shadow:0 4px 12px rgba(0,0,0,.15); font-family:sans-serif;',
  ].join('');
  panel.innerHTML = `
    <div style="margin-bottom:8px">
      <label style="font-size:11px;font-weight:600;display:block;margin-bottom:3px">Image URL</label>
      <input data-img-url type="text" placeholder="https://example.com/image.jpg"
             style="width:100%;box-sizing:border-box;padding:5px 7px;
                    border:1px solid #ccc;border-radius:3px;font-size:12px;">
    </div>
    <div style="margin-bottom:10px">
      <label style="font-size:11px;font-weight:600;display:block;margin-bottom:3px">Width</label>
      <select data-img-width
              style="width:100%;padding:5px 7px;border:1px solid #ccc;border-radius:3px;font-size:12px;">
        <option value="25">25%</option>
        <option value="33">33%</option>
        <option value="50">50%</option>
        <option value="75">75%</option>
        <option value="100" selected>100%</option>
      </select>
    </div>
    <div style="display:flex;gap:6px">
      <button data-img-insert
              style="flex:1;padding:5px;background:#0d6efd;color:#fff;
                     border:none;border-radius:3px;cursor:pointer;font-size:12px;">
        Insert
      </button>
      <button data-img-cancel
              style="flex:1;padding:5px;background:#6c757d;color:#fff;
                     border:none;border-radius:3px;cursor:pointer;font-size:12px;">
        Cancel
      </button>
    </div>`;

  wrapper.appendChild(btn);
  wrapper.appendChild(panel);

  const urlInput  = panel.querySelector('[data-img-url]')  as HTMLInputElement;
  const widthSel  = panel.querySelector('[data-img-width]') as HTMLSelectElement;

  const close = () => { panel.style.display = 'none'; urlInput.value = ''; };
  const toggle = () => {
    const isOpen = panel.style.display !== 'none';
    panel.style.display = isOpen ? 'none' : 'block';
    if (!isOpen) setTimeout(() => urlInput.focus(), 0);
  };

  btn.addEventListener('mousedown', (e) => { e.preventDefault(); toggle(); });

  panel.querySelector('[data-img-insert]')!.addEventListener('mousedown', (e) => {
    e.preventDefault();
    const src = urlInput.value.trim();
    if (!src) return;
    const style = `width: ${widthSel.value}%;`;
    const node = view.state.schema.nodes['image'].create({ src, style, alt: '' });
    view.dispatch(view.state.tr.replaceSelectionWith(node));
    view.focus();
    close();
  });

  panel.querySelector('[data-img-cancel]')!.addEventListener('mousedown', (e) => {
    e.preventDefault();
    close();
  });

  document.addEventListener('mousedown', (e) => {
    if (!wrapper.contains(e.target as Node)) close();
  });

  return {
    dom: wrapper,
    update(_state: EditorState) {},
  };
};
